import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ValidatorInput } from '../model.module/validation/validation-descriptor.entity';
import { fromEvent, Subscription, tap } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { ErweiterteAuftraggeberSucheObservable } from '../services.module/message.observable';
import { ErweiterteAuftraggeberSucheModel } from '../model.module/modals.model';
import { AuftraggeberApiService } from '../../generated/api/auftraggeber/auftraggeber.service';
import { Auftraggeber, AuftraggeberSucheResponse } from '../../generated/api/kreditportalBackendAPI.schemas';

@Component({
    selector: 'auftraggeber-suche',
    inputs: ['inputElemId', 'placeholder', 'maxlength', 'title', 'disabled'],
    templateUrl: './auftraggeber-suche.component.html',
    styleUrls: ['./auftraggeber-suche.component.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AuftraggeberSucheComponent implements AfterViewInit, OnDestroy {

    @ViewChild('inputBox', { static: false })
    inputBox?: ElementRef;

    @ViewChild('suggestionBox', { static: false })
    suggestionBox?: ElementRef;

    @Input()
    fieldModel?: ValidatorInput;

    @Output()
    searchResultSelected = new EventEmitter<Auftraggeber>();

    @Output()
    blur = new EventEmitter<void>();

    inputElemId?: string;

    maxlength?: string;

    placeholder = '';

    title = '';

    disabled = false;

    value?: string;

    isLoading = signal(false);

    shallShowSuggestionBox = false;

    numberOfAdditionalSearchResults = 0;

    filteredSuggestions: Auftraggeber[] = [];

    private subscriptionEvent1: Subscription;
    private subscriptionEvent2: Subscription;

    constructor(
        private readonly changeDetector: ChangeDetectorRef,
        private readonly erweiterteAuftraggeberSucheObservable: ErweiterteAuftraggeberSucheObservable,
        private readonly auftraggeberApiService: AuftraggeberApiService
    ) {
    }

    ngAfterViewInit(): void {
        const debounceTimeMs = 250;
        const minInputChars = 2;

        this.subscriptionEvent1 = fromEvent(this.inputBox?.nativeElement, 'input')
            .pipe(
                // Arbeite mit dem Eingabewert des Input-Felds
                map((e: any) => e.target.value),
                // Reagiere erst debounceTimeMs nach dem letzten Tastenanschlag
                debounceTime(debounceTimeMs),
                // Reagiere nur wenn mehr als minInputChars Zeichen eingegeben wurden
                filter(query => query.length >= minInputChars),
                // Reagiere nur, wenn die Eingabe sich wirklich geändert hat
                distinctUntilChanged(),
                tap(() => this.isLoading.set(true)),
                // Führe mit der Eingabe eine Promise (den Request) aus
                switchMap(query => this.auftraggeberApiService.auftraggeberSuche({ name: query, plz: '', limit: true }))
            )
            .subscribe((response: AuftraggeberSucheResponse) => {
                this.isLoading.set(false);

                this.filteredSuggestions = response.auftraggeber ?? [];
                this.numberOfAdditionalSearchResults = response.numRemainingAuftraggeber ?? 0;

                if (response.numFoundAuftraggeber && response.numFoundAuftraggeber > 0) {
                    this.showSuggestionBox();
                } else {
                    this.hideSuggestionBox();
                }
            });

        this.subscriptionEvent2 = fromEvent(this.inputBox?.nativeElement, 'input')
            .pipe(
                // Arbeite mit dem Eingabewert des Input-Felds
                map((e: any) => e.target.value)
            )
            .subscribe((inputValue: string) => {
                // Verstecke Vorschlagsliste, wenn weniger als minInputChars Zeichen eingegeben wurden
                if (inputValue.length < minInputChars) {
                    this.hideSuggestionBox();
                }
            });
    }

    ngOnDestroy(): void {
        if (this.subscriptionEvent1) {
            this.subscriptionEvent1.unsubscribe();
        }

        if (this.subscriptionEvent2) {
            this.subscriptionEvent2.unsubscribe();
        }
    }

    onInputFieldKeyDown(event: KeyboardEvent) {
        switch (event.key) {
            case 'ArrowUp':
                event.preventDefault();
                this.focusLastSuggestion();
                break;
            case 'ArrowDown':
                event.preventDefault();
                this.focusFirstSuggestion();
                break;
            case 'Escape':
                this.hideSuggestionBox();
                break;
        }
    }

    onInputFieldBlur(event: FocusEvent) {
        // Wenn der Fokus an ein autocomplete-suggestions__element übergeben wird, schließe nicht die Vorschlagsliste
        const relatedTarget = event.relatedTarget as HTMLElement;
        if (relatedTarget && relatedTarget.classList.contains('autocomplete-suggestions__element')) {
            return;
        }

        // Wenn das Input-Feld nicht mehr fokussiert ist, schließe Vorschlagsliste
        this.hideSuggestionBox();

        this.blur.emit();
    }

    onSuggestionElementMouseDown(suggestion: Auftraggeber) {
        // Anmerkung: Dies muss Event mousedown statt click sein, weil mousedown vorm blur des Input-Felds aufgerufen wird
        // https://stackoverflow.com/a/39439174
        this.hideSuggestionBox();
        this.notifyAuftraggeberSelected(suggestion);
    }

    onSuggestionElementKeyDown(event: KeyboardEvent, suggestion: Auftraggeber) {
        switch (event.key) {
            case 'Enter':
                this.hideSuggestionBox();
                this.notifyAuftraggeberSelected(suggestion);
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.focusPreviousSuggestion();
                break;
            case 'ArrowDown':
                event.preventDefault();
                this.focusNextSuggestion();
                break;
        }
    }

    onErweiterteSucheBtnClick() {
        this.hideSuggestionBox();

        const erweiterteAuftraggeberSucheModel = new ErweiterteAuftraggeberSucheModel(this.fieldModel?.value);
        this.erweiterteAuftraggeberSucheObservable.notify(erweiterteAuftraggeberSucheModel);
    }

    onCloseBtnClick() {
        this.hideSuggestionBox();
    }

    private showSuggestionBox() {
        this.shallShowSuggestionBox = true;
        this.changeDetector.detectChanges();
    }

    private hideSuggestionBox() {
        this.shallShowSuggestionBox = false;
        this.changeDetector.detectChanges();
    }

    private notifyAuftraggeberSelected(suggestion: Auftraggeber) {
        this.searchResultSelected.emit(suggestion);
    }

    private focusFirstSuggestion() {
        const children = this.suggestionBox?.nativeElement.children;

        if (!children || children.length === 0) {
            return;
        }

        children[0].focus();
    }

    private focusLastSuggestion() {
        const children = this.suggestionBox?.nativeElement.children;

        if (!children || children.length < 2) {
            return;
        }

        children[children.length - 2].focus();
    }

    private focusPreviousSuggestion() {
        const previousSibling = document.activeElement?.previousElementSibling as HTMLElement;

        if (!previousSibling) {
            this.focusLastSuggestion();
            return;
        }

        previousSibling.focus();
    }

    private focusNextSuggestion() {
        const nextSibling = document.activeElement?.nextElementSibling as HTMLElement;

        if (!nextSibling || nextSibling.classList.contains('autocomplete-suggestions__footer')) {
            this.focusFirstSuggestion();
            return;
        }

        nextSibling.focus();
    }

}
